import axios from "axios";
import { createContext, createElement, useCallback, useContext, useEffect, useState } from "react";
import { BACKEND } from "../lib/api";
import { useI18n } from "../lib/i18n";

export const aapi = axios.create({ baseURL: `${BACKEND}/api`, withCredentials: true });

let refreshing = null;
aapi.interceptors.response.use(null, async (err) => {
  const cfg = err.config;
  if (err.response?.status === 401 && !cfg._retry && !cfg.url.startsWith("/auth/")) {
    cfg._retry = true;
    refreshing = refreshing || aapi.post("/auth/refresh").finally(() => { refreshing = null; });
    try { await refreshing; return aapi(cfg); } catch { window.dispatchEvent(new Event("lf-logout")); }
  }
  return Promise.reject(err);
});

export const apiErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return e?.message || "Error";
};

const AuthCtx = createContext(null);
const STAFF_SESSION = "lf_staff_session";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const load = useCallback(() => {
    if (!localStorage.getItem(STAFF_SESSION)) { setUser(false); return; }
    aapi.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem(STAFF_SESSION); setUser(false); });
  }, []);
  useEffect(() => {
    load();
    const out = () => { localStorage.removeItem(STAFF_SESSION); setUser(false); };
    window.addEventListener("lf-logout", out);
    return () => window.removeEventListener("lf-logout", out);
  }, [load]);
  const login = async (email, password) => { const r = await aapi.post("/auth/login", { email, password }); localStorage.setItem(STAFF_SESSION, "1"); setUser(r.data.user); return r.data.user; };
  const logout = async () => { localStorage.removeItem(STAFF_SESSION); await aapi.post("/auth/logout").catch(() => {}); setUser(false); };
  return createElement(AuthCtx.Provider, { value: { user, login, logout } }, children);
}

export const useAuth = () => useContext(AuthCtx);

export function useA() {
  const { lang } = useI18n();
  return (en, ar) => (lang === "ar" ? ar || en : en);
}

export const SA = "super_admin", MG = "manager", AG = "sales_agent", ED = "content_editor";
export const CONTENT = [SA, MG, ED];
export const can = (user, roles) => !!user && roles.includes(user.role);

export async function download(path, params, fallbackName) {
  const r = await aapi.request({ url: path, method: path.includes("/backups") && !path.includes("download") ? "post" : "get", params, responseType: "blob" });
  const name = (r.headers["content-disposition"] || "").match(/filename="(.+)"/)?.[1] || fallbackName;
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function useAdminApi(path, deps = []) {
  const [state, set] = useState({ data: null, loading: true, error: null });
  const reload = useCallback(() => {
    if (!path) return;
    set((s) => ({ ...s, loading: true }));
    aapi.get(path).then((r) => set({ data: r.data, loading: false, error: null })).catch((e) => set({ data: null, loading: false, error: e }));
  }, [path]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [reload, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  return { ...state, reload };
}

export const fmtDT = (iso, lang) => (iso ? new Intl.DateTimeFormat(lang === "ar" ? "ar-SA-u-ca-gregory" : "en-GB", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso)) : "");
